import React, { useEffect, useState } from "react";
import { api, fmtMoney, STAGES } from "../lib/api";
import { TrendingUp, DollarSign, Briefcase, Target as TargetIcon, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import Leaderboard from "../components/Leaderboard";
import Logo from "../components/Logo";
import YearEndReport from "../components/YearEndReport";
import { useAuth } from "../context/AuthContext";

const STAGE_COLORS = {
  Prospect: "#d1cece", CMA: "#e1b7c1", Listed: "#e1b7c1",
  Contract: "#bf858e", Closing: "#bf858e", Closed: "#050303",
};

export default function Dashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [deals, setDeals] = useState([]);

  useEffect(() => {
    Promise.all([api.get("/dashboard"), api.get("/deals")])
      .then(([d, ds]) => { setData(d.data); setDeals(ds.data || []); });
  }, []);

  if (!data) return <div data-testid="dashboard-loading">Loading…</div>;

  const priority = deals
    .filter(d => d.stage === "Contract" || d.stage === "Closing")
    .slice(0, 5);

  return (
    <div className="space-y-10" data-testid="dashboard-page">
      <header className="flex items-start justify-between gap-6 flex-wrap">
        <div>
          <div className="overline">Command Center</div>
          <h1 className="font-heading text-5xl mt-2 tracking-tight">Hello CORE Agent</h1>
          <ClockDate />
          <p className="text-[#050303]/70 mt-3 text-sm">Here's how your week is closing out at CORE.</p>
        </div>
        <div className="flex flex-col items-end gap-3">
          <Logo variant="black" height={64} className="opacity-90" />
          <YearEndReport />
        </div>
      </header>

      <section className="grid grid-cols-1 md:grid-cols-3 gap-6" data-testid="dashboard-metrics">
        <Metric icon={<DollarSign size={18} strokeWidth={1.5} />} label="Pipeline Value" value={fmtMoney(data.pipeline_value)} accent />
        <Metric icon={<TrendingUp size={18} strokeWidth={1.5} />} label="GCI YTD" value={fmtMoney(data.gci_ytd)} sub={`Goal ${fmtMoney(data.gci_goal)}`} progress={data.gci_progress_pct} />
        <Metric icon={<Briefcase size={18} strokeWidth={1.5} />} label="Active Deals" value={data.deals_total} sub="Across 6 stages" />
      </section>

      <section>
        <div className="core-card p-7">
          <div className="flex items-center justify-between mb-5">
            <div>
              <div className="overline">Today's Priority Actions</div>
              <h3 className="font-heading text-2xl mt-1">Close the week strong.</h3>
            </div>
            <Link to="/pipeline" className="text-xs uppercase tracking-wider text-[#bf858e] hover:text-[#050303] flex items-center gap-1">View pipeline <ArrowRight size={13} /></Link>
          </div>
          <ul className="divide-y divide-[#d1cece]" data-testid="priority-actions">
            {priority.length === 0 && <li className="py-4 text-sm text-[#050303]/50">No urgent contract/closing deals.</li>}
            {priority.map(d => (
              <li key={d.id} className="py-3 flex items-center justify-between gap-4">
                <div className="min-w-0">
                  <div className="font-semibold text-sm truncate">{d.title}</div>
                  <div className="text-xs text-[#050303]/55 truncate">{d.address}</div>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <span className="text-xs uppercase tracking-wider px-2.5 py-1 rounded-full"
                    style={{ background: STAGE_COLORS[d.stage] + "55", color: "#050303" }}>{d.stage}</span>
                  <span className="text-sm font-semibold">{fmtMoney(d.price)}</span>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className={`grid grid-cols-1 gap-6 ${user?.role === "admin" ? "lg:grid-cols-3" : ""}`}>
        {user?.role === "admin" && <Leaderboard />}
        <div className={`core-card p-7 ${user?.role === "admin" ? "lg:col-span-2" : ""}`} data-testid="stage-funnel">
        <div className="overline">Conversion Funnel</div>
        <h3 className="font-heading text-2xl mt-1 mb-6">Pipeline by stage</h3>
        <div className="grid grid-cols-6 gap-3">
          {STAGES.map(s => (
            <div key={s} className="text-center">
              <div className="h-24 rounded-lg flex items-end justify-center pb-3"
                style={{ background: STAGE_COLORS[s] + "30", borderBottom: `3px solid ${STAGE_COLORS[s]}` }}>
                <span className="font-heading text-3xl">{data.by_stage?.[s] || 0}</span>
              </div>
              <div className="overline mt-2 text-[10px]">{s}</div>
            </div>
          ))}
        </div>
        </div>
      </section>
    </div>
  );
}

import { useEffect as _use, useState as _useState } from "react";

function ClockDate() {
  const [now, setNow] = _useState(new Date());
  _use(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);
  const time = now.toLocaleTimeString("en-US", { timeZone: "America/New_York", hour: "numeric", minute: "2-digit", second: "2-digit", hour12: true });
  const date = now.toLocaleDateString("en-US", { timeZone: "America/New_York", weekday: "long", month: "long", day: "numeric", year: "numeric" });
  return (
    <div className="mt-3 flex flex-wrap items-baseline gap-3" data-testid="clock-date">
      <span className="font-heading text-2xl tabular-nums text-[#bf858e]">{time}</span>
      <span className="text-xs uppercase tracking-wider text-[#050303]/60">EST</span>
      <span className="text-sm text-[#050303]/75">· {date}</span>
    </div>
  );
}

function Metric({ icon, label, value, sub, progress, accent }) {
  return (
    <div className={`core-card p-7 relative overflow-hidden ${accent ? "bg-gradient-to-br from-white to-[#e1b7c1]/25" : ""}`} data-testid={`metric-${label.toLowerCase().replace(/ /g, "-")}`}>
      <div className="flex items-center justify-between mb-5">
        <div className="overline">{label}</div>
        <div className="text-[#bf858e]">{icon}</div>
      </div>
      <div className="font-heading text-5xl tracking-tight">{value}</div>
      {sub && <div className="text-xs text-[#050303]/55 mt-2">{sub}</div>}
      {progress != null && (
        <div className="mt-4 h-1.5 bg-[#d1cece]/40 rounded-full overflow-hidden">
          <div className="h-full bg-[#050303] rounded-full" style={{ width: `${progress}%` }} />
        </div>
      )}
    </div>
  );
}
